import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Send, Loader2, Briefcase, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useModalClose, CLOSE_BTN_STYLE } from '../../hooks/useModalClose';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hoisted out of the component to satisfy react/no-unstable-nested-components.
// Receives the controlled `type` + setter explicitly so it never closes over
// stale parent state.
const TypePill = ({ value, label, icon: Icon, type, setType }) => (
  <button
    type="button"
    onClick={() => setType(value)}
    data-testid={`engagement-type-${value}`}
    className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border transition-all ${
      type === value ? 'bg-cn-primary text-white border-cn-primary shadow-sm' : 'bg-white text-cn-text border-cn-border hover:border-cn-primary/40'
    }`}
  >
    <Icon className="w-4 h-4" /> {label}
  </button>
);

/**
 * Agency → Clinician engagement request (temporary / permanent staffing).
 * Connect-only: no payment is processed here.
 */
const EngagementModal = ({ open, onClose, clinician, getAuthHeader, onSent }) => {
  const [type, setType] = useState('temporary');
  const [title, setTitle] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [location, setLocation] = useState('');
  const [duration, setDuration] = useState('');
  const [rate, setRate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setType('temporary'); setTitle(''); setSpecialty(''); setLocation('');
      setDuration(''); setRate(''); setStartDate(''); setDescription('');
    }
  }, [open]);

  // Hooks must run on every render — call before any early-return.
  // Lock dismissal while submission is in-flight so we don't lose the
  // user's form data on a stray ESC / backdrop tap during the API call.
  const { backdropProps, cardProps } = useModalClose({
    open: !!open,
    onClose: submitting ? () => {} : onClose,
    dismissOnEscape: !submitting,
    dismissOnBackdrop: !submitting,
  });

  if (!open || !clinician) return null;

  const submit = async () => {
    if (!title.trim()) { toast.error('Add a short title for this opportunity'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/engagements`, {
        clinician_id: clinician.id,
        engagement_type: type,
        title,
        specialty: specialty || null,
        location: location || null,
        duration: duration || null,
        rate: rate || null,
        start_date: startDate || null,
        description: description || null,
      }, getAuthHeader());
      toast.success(`Engagement request sent to ${clinician.name}`);
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in-up" {...backdropProps} data-testid="engagement-modal">
      <div className="relative w-full max-w-lg rounded-3xl bg-white border border-cn-border shadow-2xl overflow-hidden max-h-[90vh] flex flex-col" {...cardProps}>
        <div className="p-5 text-white flex items-start justify-between" style={{ background: 'linear-gradient(135deg, #0F172A, #334155)' }}>
          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-white/70">Staffing request</p>
            <h3 className="font-display font-extrabold text-xl">Engage {clinician.name}</h3>
            <p className="text-white/75 text-xs mt-1">Connect directly — contracts &amp; pay are handled between you and the clinician.</p>
          </div>
          <button type="button" onClick={onClose} disabled={submitting} aria-label="Close" className="flex items-center justify-center rounded-full hover:bg-white/15 active:bg-white/25 transition-colors touch-manipulation disabled:opacity-50" style={CLOSE_BTN_STYLE} data-testid="engagement-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="flex gap-2">
            <TypePill value="temporary" label="Temporary / Locum" icon={Clock} type={type} setType={setType} />
            <TypePill value="permanent" label="Permanent" icon={Briefcase} type={type} setType={setType} />
          </div>

          <div>
            <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Opportunity title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. 13-week ICU night contract" className="mt-1.5 h-11 rounded-xl border-cn-border" data-testid="engagement-title" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Specialty</Label>
              <Input value={specialty} onChange={(e) => setSpecialty(e.target.value)} placeholder="ICU, Geriatrics…" className="mt-1.5 h-11 rounded-xl border-cn-border" data-testid="engagement-specialty" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Location</Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Austin, TX / Remote" className="mt-1.5 h-11 rounded-xl border-cn-border" data-testid="engagement-location" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Duration</Label>
              <Input value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="13 weeks" className="mt-1.5 h-11 rounded-xl border-cn-border" data-testid="engagement-duration" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Rate / Salary</Label>
              <Input value={rate} onChange={(e) => setRate(e.target.value)} placeholder="$85/hr" className="mt-1.5 h-11 rounded-xl border-cn-border" data-testid="engagement-rate" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Start</Label>
              <Input value={startDate} onChange={(e) => setStartDate(e.target.value)} placeholder="ASAP / Aug 1" className="mt-1.5 h-11 rounded-xl border-cn-border" data-testid="engagement-start" />
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Details</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Shift pattern, unit, requirements, benefits…" className="mt-1.5 min-h-[88px] rounded-xl border-cn-border" data-testid="engagement-description" />
          </div>
        </div>

        <div className="p-4 border-t border-cn-border bg-cn-bone-100">
          <button onClick={submit} disabled={submitting} className="cn-btn cn-btn-primary w-full h-11 disabled:opacity-60" data-testid="engagement-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send engagement request</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EngagementModal;
